/**
 * Copyright (c) 2010-2020 California CAD Solutions, Inc. Licensed subject
 * to an End-User License Agreement included in a separate file named EULA.txt
 * that is distributed with this file.
 */
'use strict';

import Feature from 'ol/Feature';
import GeoJSON from 'ol/format/GeoJSON';
import Vector from 'ol/layer/Vector';
import Stroke from 'ol/style/Stroke';
import Style from 'ol/style/Style';
import Fill from 'ol/style/Fill';
import Circle from 'ol/style/Circle';

import { Injectable } from '@angular/core';

import {
    catchError,
    concatMap,
    distinctUntilChanged,
    filter,
    first,
    map,
    mergeMapTo,
    take,
    withLatestFrom,
} from 'rxjs/operators';
import { MapConfigService } from '../../../modules/map/services/mapConfig.service';

import { DataFormService } from '../../../modules/reports/services/dataform.service';
import { RestService } from '../../../modules/shared/services/rest.service';
import { MapService } from '../../../modules/map/services/map.service';
import { MapInteractionsService } from '../../../modules/map/services/mapInteractions.service';
import { LoadRedlineConfig } from '../state/redline.actions';
import { Actions, ofActionSuccessful, Select, Store } from '@ngxs/store';
import { RedlineState } from '../state/redline.state';
import { combineLatest, Observable, of } from 'rxjs';
import { RedlineLayerConfiguration } from '../state/redline.model';
import { LoadDataFormPayload } from '../../../modules/reports/state/dataForm.model';
import { MatDialog } from '@angular/material/dialog';
import { DataFormDialogComponent } from '../../../modules/reports/dataForm/data-form-dialog.component';
import { LoggerService } from '../../../modules/shared/services/logger.service';
import { LoadDataForm } from '../../../modules/reports/state/dataForm.actions';
import { RedlineConfigService } from './redline-config.service';
import { ActiveMapState } from '../../../modules/map/state/active-map.state';
import VectorSource from 'ol/source/Vector';

@Injectable()
export class RedlineService {
    @Select(RedlineState.viewable) viewableLayers$: Observable<RedlineLayerConfiguration[]>;
    @Select(RedlineState.editable) editableLayers$: Observable<RedlineLayerConfiguration[]>;

    private _redlineQueue: any[];

    /**
     * @param logger {LoggerService}
     * @param rest {RestService}
     * @param mapInteractions {MapInteractionsService}
     * @param mapService {MapService}
     * @param redlineConfig {RedlineConfigService}
     * @param mapConfig {MapConfigService}
     * @param dataFormService {DataFormService}
     * @param actions$ {Actions}
     * @param store {Store}
     * @param dialog
     */
    constructor(
        private logger: LoggerService,
        private rest: RestService,
        private mapInteractions: MapInteractionsService,
        private mapService: MapService,
        private redlineConfig: RedlineConfigService,
        private mapConfig: MapConfigService,
        private dataFormService: DataFormService,
        private actions$: Actions,
        private store: Store,
        private dialog: MatDialog
    ) {
        this._redlineQueue = [];

        const mapCreated$ = this.store.select(ActiveMapState.created).pipe(
            catchError((err) => {
                this.logger.error('redline.service.ts:84', err);
                return of(false);
            }),
            distinctUntilChanged(),
            filter((b) => b)
        );

        const configLoaded$ = this.actions$.pipe(ofActionSuccessful(LoadRedlineConfig));

        combineLatest([mapCreated$, configLoaded$])
            .pipe(
                map(([created, _]: [boolean, any]) => created),
                filter((b) => b),
                mergeMapTo(this.viewableLayers$.pipe(take(1)))
            )
            .subscribe((viewableLayers) => {
                if (viewableLayers.length > 0) {
                    this.mapService.createRedlineLayers(viewableLayers, this.redlineFeatureLoader.bind(this));
                }
            });
    }

    /**
     * Add a feature to the redline layer and trigger a map redraw
     * @param {string} layerName
     * @param {ol.Feature} feature
     */
    public addLayerFeature(layerName: string, feature: Feature): void {
        const layer: Vector<VectorSource> = this.mapService.getVectorLayer(layerName);
        const source = layer.getSource();
        source.addFeature(feature);
        feature.changed();
    }

    public drawFeature(
        layerName: string,
        initialSelection = null,
        getDrawEventFn = this.getDefaultDrawEndFn.bind(this)
    ) {
        this.redlineConfig.editableLayers$
            .pipe(
                map((layers) => layers.find((l) => l.name === layerName)),
                take(1)
            )
            .subscribe((layer) => {
                switch (layer.type) {
                    case 'line':
                        this.drawLineFeature(layerName, initialSelection, getDrawEventFn);
                        break;
                    case 'point':
                        this.drawPointFeature(layerName, initialSelection, getDrawEventFn);
                }
            });
    }

    public saveFeature(layerName, drawnFeature) {
        const layer = this.mapService.getVectorLayer(layerName);
        if (layer) {
            const geo = new GeoJSON({ geometryName: 'geometry' });
            const featureString = geo.writeFeature(drawnFeature);
            const route = `map/redline/save/${this.mapConfig.mapName}/${layerName}`;
            return this.rest.apiPost(route, featureString).pipe(
                map((data: object) => {
                    const savedFeature = geo.readFeature(data);
                    this.addLayerFeature(layerName, savedFeature);
                    this._redlineQueue.push({
                        layer: layerName,
                        feature: savedFeature,
                    });
                    return savedFeature;
                })
            );
        }
    }

    public deleteFeature(layerName, feature) {
        let apiResult = null;
        const layer = this.mapService.getVectorLayer(layerName);
        if (layer) {
            const id = feature.getId();
            const route = `map/redline/delete/${this.mapConfig.mapName}/${layerName}/${id}`;
            apiResult = this.rest.apiPost(route, null);
        }
        return apiResult;
    }

    public undoRedline() {
        if (this._redlineQueue.length > 0) {
            const queueEntry = this._redlineQueue[this._redlineQueue.length - 1];
            const layerName = queueEntry.layer;
            const layer = this.mapService.getVectorLayer(layerName);
            const feature = queueEntry.feature;
            if (layer) {
                this.deleteFeature(layerName, feature).subscribe((result) => {
                    if (result === true) {
                        const source = layer.getSource();
                        const removalEntry = this._redlineQueue.pop();
                        const removalFeature = removalEntry.feature;
                        // get the feature by its id
                        const sourceFeature = source.getFeatureById(removalFeature.getId());
                        source.removeFeature(sourceFeature);
                        source.refresh();
                    }
                });
            }
        }
    }

    private drawPointFeature(layerName, initialSelection = null, getDrawEventFn) {
        const interactionOptions = {
            clickTolerance: 4,
            style: new Style({
                image: new Circle({
                    fill: new Fill({ color: '#0A0AA0' }),
                    radius: 2,
                    stroke: new Stroke({
                        color: '#0A0AA0',
                        width: 0,
                    }),
                }),
            }),
            type: 'Point',
        };

        const drawEventFn = getDrawEventFn(layerName, initialSelection);
        this.redlineConfig.editableLayers$
            .pipe(
                first(),
                map((editableLayers) => editableLayers.find((l) => l.name === layerName))
            )
            .subscribe((editableLayer) => {
                this.mapInteractions.enableRedline(editableLayer.type, interactionOptions, drawEventFn);
            });
    }

    private drawLineFeature(layerName, initialSelection = null, getDrawEventFn) {
        const interactionOptions = {
            clickTolerance: 4,
            style: new Style({
                fill: null,
                stroke: new Stroke({
                    color: '#0A0AA0',
                    width: 1,
                }),
            }),
            type: 'LineString',
        };

        const drawEventFn = getDrawEventFn(layerName, initialSelection);
        this.editableLayers$
            .pipe(
                first(),
                map((editableLayers) => editableLayers.find((l) => l.name === layerName))
            )
            .subscribe((editableLayer) => {
                this.mapInteractions.enableRedline(editableLayer.type, interactionOptions, drawEventFn);
            });
    }

    private getDefaultDrawEndFn(layerName, initialSelection) {
        const layerConfig$ = this.viewableLayers$.pipe(map((layers) => layers.find((l) => l.name === layerName)));
        return (drawEvent) => {
            this.saveFeature(layerName, drawEvent.feature)
                .pipe(
                    withLatestFrom(layerConfig$),
                    concatMap(([savedFeature, layerConfig]: [Feature, RedlineLayerConfiguration]) => {
                        const dataFormPayload: LoadDataFormPayload = {
                            name: layerConfig.dataform,
                            keys: [savedFeature.getId()],
                            initialSelection,
                        };
                        return this.store.dispatch(new LoadDataForm(dataFormPayload));
                    }),
                    concatMap(() => layerConfig$)
                )
                .subscribe((layerConfig) => {
                    const dialogRef = this.dialog.open(DataFormDialogComponent, { data: layerConfig.dataform });
                    dialogRef.afterClosed().subscribe((result) => {
                        if (result) {
                        }
                    });
                });
        };
    }

    /**
     * @param name {string}
     */
    private redlineFeatureLoader(name) {
        const route = `map/redline/features/${this.mapConfig.mapName}/${name}`;
        const apiLoader = this.rest.apiGet.bind(this.rest, route);
        // noinspection TsLint
        return function () {
            // 'this' will be ol.Source.Vector in this fn.
            apiLoader().subscribe((data) => {
                if (data && data.collection) {
                    const features = this.getFormat().readFeatures(data.collection);
                    const existingFeatures = this.getFeatures();
                    existingFeatures.forEach((current: Feature) => {
                        if (features.findIndex((x) => current.getId() === x.getId()) === -1) {
                            this.removeFeature(current);
                        }
                    });
                    this.addFeatures(features);
                }
            });
        };
    }

    private getCurrentRedlineLayer$(): Observable<string> {
        return this.store.select(RedlineState.current);
    }

    get hasFeatures() {
        return this._redlineQueue.length > 0;
    }

    get canEdit$(): Observable<boolean> {
        return this.redlineConfig.editableLayers$.pipe(map((layers: RedlineLayerConfiguration[]) => layers.length > 0));
    }

    get layersVisible$(): Observable<boolean> {
        return this.redlineConfig
            .getVisibleLayerList$(this.mapService.currentScale)
            .pipe(map((layers: RedlineLayerConfiguration[]) => layers.length > 0));
    }
}
